import React from "react";
import { Spinner } from "../../../components";
import { useAPI } from "../../../providers/ApiProvider";
import { cn } from "../../../utils/bem";
import "./Config.prefix.css";
import { IconInfo } from "@humansignal/icons";
import { Button, EnterpriseBadge } from "@humansignal/ui";
import { useTranslation } from "../../../i18n/LocaleProvider";

const listClass = cn("templates-list");

// Inline SVG placeholder used when a template's image is missing or 404s.
// Used until proper thumbnails are designed and dropped into
// label_studio/core/static/templates/.
const TEMPLATE_PLACEHOLDER = `data:image/svg+xml;utf8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 80">
    <rect width="120" height="80" rx="6" fill="#f3f4f6"/>
    <rect x="18" y="22" width="84" height="6" rx="3" fill="#d1d5db"/>
    <rect x="18" y="34" width="60" height="6" rx="3" fill="#d1d5db"/>
    <rect x="18" y="46" width="72" height="6" rx="3" fill="#d1d5db"/>
    <rect x="18" y="58" width="40" height="6" rx="3" fill="#d1d5db"/>
  </svg>`,
)}`;

const Arrow = () => {
  const { t } = useTranslation();
  return (
    <svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <title>{t("misc.arrow_icon")}</title>
      <path opacity="0.9" d="M2 10L6 6L2 2" stroke="currentColor" strokeWidth="2" strokeLinecap="square" />
    </svg>
  );
};

const TemplatesInGroup = ({ templates, group, onSelectRecipe, isEdition }) => {
  const isCommunityEdition = isEdition === "Community";
  const picked = templates
    .filter((recipe) => recipe.group === group)
    // Upstream "enterprise" recipes (Chat, …) can never be used here —
    // the tags behind them are stubs — so don't advertise them at all.
    .filter((recipe) => !(isCommunityEdition && recipe.type === "enterprise"))
    // templates without `order` go to the end of the list
    .sort((a, b) => (a.order ?? Number.POSITIVE_INFINITY) - (b.order ?? Number.POSITIVE_INFINITY));

  return (
    <ul>
      {picked.map((recipe) => {
        const isEnterpriseTemplate = recipe.type === "enterprise";
        const isDisabled = isCommunityEdition && isEnterpriseTemplate;

        return (
          <li
            key={recipe.title}
            onClick={() => !isDisabled && onSelectRecipe(recipe)}
            className={listClass.elem("template").mod({ disabled: isDisabled }).toClassName()}
            title={isDisabled ? "Enterprise feature - Available in Label Studio Enterprise" : ""}
          >
            <img
              src={recipe.image || TEMPLATE_PLACEHOLDER}
              alt=""
              onError={(e) => {
                if (e.currentTarget.src !== TEMPLATE_PLACEHOLDER) {
                  e.currentTarget.src = TEMPLATE_PLACEHOLDER;
                }
              }}
            />
            <div className="flex flex-col items-center w-full">
              <h3 className="flex flex-1 justify-center text-center w-full">{recipe.title}</h3>
              {isEnterpriseTemplate && isCommunityEdition && <EnterpriseBadge className="mb-base" />}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

const USER_GROUP = "Your org's templates";

/** The sidebar splits the gallery in two: templates that gather new data
 *  (record a voice, sign, take a photo) and templates that label data
 *  already imported, listed first. Collection groups are named "... Collection". */
const isCollectionGroup = (group) => /\bCollection$/.test(group);

const sectionGroups = (groups) => {
  const own = groups.filter((g) => g === USER_GROUP);
  const rest = groups.filter((g) => g !== USER_GROUP);
  return [
    { title: null, groups: own },
    { title: "Annotate data", groups: rest.filter((g) => !isCollectionGroup(g)) },
    { title: "Collect data", groups: rest.filter(isCollectionGroup) },
  ].filter((section) => section.groups.length);
};

/** Shape a saved ProjectTemplate into the recipe format the wizard expects
 *  and pass through the extra fields (instructions, sampling, regions rules)
 *  for the Config step to apply to the draft project on pick. */
const savedTemplateToRecipe = (t) => ({
  title: t.name,
  group: USER_GROUP,
  order: -(new Date(t.updated_at).valueOf() || 0), // newest first
  details: t.description || "",
  config: t.label_config || "<View></View>",
  type: "community",
  image: null,
  __savedTemplateId: t.id,
  __savedTemplateFields: {
    expert_instruction: t.expert_instruction,
    show_instruction: t.show_instruction,
    show_skip_button: t.show_skip_button,
    enable_empty_annotation: t.enable_empty_annotation,
    maximum_annotations: t.maximum_annotations,
    sampling: t.sampling,
    regions_min_per_annotation: t.regions_min_per_annotation,
    regions_max_per_annotation: t.regions_max_per_annotation,
  },
});

export const TemplatesList = ({ selectedGroup, selectedRecipe, onCustomTemplate, onSelectGroup, onSelectRecipe }) => {
  const { t } = useTranslation();
  const [groups, setGroups] = React.useState([]);
  const [templates, setTemplates] = React.useState();
  const api = useAPI();
  const isEdition = window?.APP_SETTINGS?.version_edition;

  React.useEffect(() => {
    const fetchData = async () => {
      // Fetch built-in configs + user/org saved templates in parallel. Saved
      // templates are gated by projects_create; if the caller can't see
      // them the API returns empty, which is fine.
      const [builtIn, userTemplates] = await Promise.all([
        api.callApi("configTemplates"),
        api.callApi("projectTemplates", { suppressError: true }),
      ]);
      if (!builtIn) return;
      const savedRecipes = Array.isArray(userTemplates?.results)
        ? userTemplates.results.map(savedTemplateToRecipe)
        : [];
      const mergedTemplates = [...savedRecipes, ...(builtIn.templates || [])];
      const mergedGroups = savedRecipes.length ? [USER_GROUP, ...(builtIn.groups || [])] : builtIn.groups || [];
      setTemplates(mergedTemplates);
      setGroups(mergedGroups);
    };
    fetchData();
  }, []);

  const selected = selectedGroup || sectionGroups(groups)[0]?.groups[0];

  return (
    <div className={listClass}>
      <aside className={listClass.elem("sidebar").toClassName()}>
        {sectionGroups(groups).map((section) => (
          <section key={section.title ?? "own"} aria-label={section.title ?? USER_GROUP}>
            {section.title ? <h4 className={listClass.elem("section").toClassName()}>{section.title}</h4> : null}
            <ul>
              {section.groups.map((group) => (
                <li
                  key={group}
                  onClick={() => onSelectGroup(group)}
                  className={listClass
                    .elem("group")
                    .mod({
                      active: selected === group,
                      selected: selectedRecipe?.group === group,
                    })
                    .toClassName()}
                >
                  {group}
                  <Arrow />
                </li>
              ))}
            </ul>
          </section>
        ))}
        <Button
          type="button"
          align="left"
          look="string"
          size="small"
          onClick={onCustomTemplate}
          className="w-full"
          aria-label={t("misc.create_custom_template")}
        >
          Custom template
        </Button>
      </aside>
      {/* Phone-only category picker. The sidebar above is hidden
          ≤768px (see Config.prefix.css .templates-list mobile block)
          and this native select takes its place — a dropdown scans
          faster than a scrollable chip strip on a small screen. */}
      <div className={listClass.elem("mobile-picker").toClassName()}>
        <select
          value={selected ?? ""}
          onChange={(e) => onSelectGroup(e.target.value)}
          aria-label={t("misc.template_category")}
        >
          {sectionGroups(groups).map((section) =>
            section.title ? (
              <optgroup key={section.title} label={section.title}>
                {section.groups.map((group) => (
                  <option key={group} value={group}>
                    {group}
                  </option>
                ))}
              </optgroup>
            ) : (
              section.groups.map((group) => (
                <option key={group} value={group}>
                  {group}
                </option>
              ))
            ),
          )}
        </select>
        <Button
          type="button"
          look="outlined"
          size="small"
          onClick={onCustomTemplate}
          aria-label="Create custom template"
        >
          Custom
        </Button>
      </div>
      <main>
        {!templates && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: 200 }}>
            <Spinner size={40} />
          </div>
        )}
        <TemplatesInGroup
          templates={templates || []}
          group={selected}
          onSelectRecipe={onSelectRecipe}
          isEdition={isEdition}
        />
      </main>
      <footer className="flex items-center justify-center gap-1">
        <IconInfo className={listClass.elem("info-icon").toClassName()} width="20" height="20" />
        <span>
          See the documentation to{" "}
          <a href="https://afriannotate.waraka.org/docs/" target="_blank" rel="noreferrer">
            contribute a template
          </a>
          .
        </span>
      </footer>
    </div>
  );
};
